import { api } from 'api/api';
import { useContext, useEffect, useState, createContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTelemedicina } from 'context/TelemedicinaContext';
import { AccountContext } from 'components/AccountContext';
import { userIsSubscriber } from 'components/Utils/theme';

const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

export const AgendamentoContext = createContext();

const useModelMedicos = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [especialista, setEspecialista] = useState(null);
  const [modalEspecialista, setModalEspecialista] = useState(false);
  const [avaliacaoMedia, setAvaliacaoMedia] = useState(null);
  const navigate = useNavigate();
  const { paciente, agendamento, atualizarAgendamento, setMedicoSelecionado, setAgendamento } = useTelemedicina();
  const { user, toastInadimplente } = useContext(AccountContext);
  const [medicos, setMedicos] = useState([]);
  const [fetchMore, setFetchMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [avaliacoes_atualizadas, setAvaliacoesAtualizadas] = useState([]);
  const [proximasDatas, setProximasDatas] = useState([]);
  const assinante = userIsSubscriber(user);
  const [loadingAgendas, setLoadingAgendas] = useState(true);

  async function listarEspecialidades() {
    try {
      setLoading(true);
      const url = ENDPOINT + `/schedule/list-specialities/${paciente?.sexo}/${user.id_contrato}/${paciente.idade}`;
      const response = await api(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      });
      const postsData = await response.json();
      setEspecialista(postsData);
    } catch (error) {
      console.log('Erro ao listar especialidades:', error);
      setError('Não foi possível carregar as especialidades.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let ignore = false;
    if (!ignore && paciente) {
      listarEspecialidades();
    }
    return () => {
      ignore = true;
    };
  }, [paciente]);

  async function fetchData(url) {
    try {
      let response = await api(url, {
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
      });
      let data = await response.json();
      return data;
    } catch (error) {
      return [];
    }
  }

  async function fetchAvaliacoes(medicos) {
    if (!medicos) return [];
    // Buscar apenas avaliações de médicos que ainda não estão em avaliacoes_atualizadas
    const idsExistentes = new Set(avaliacoes_atualizadas.map((a) => a.operador_id));
    const medicosNovos = medicos.filter((medico) => !idsExistentes.has(medico.dados.medico_id));
    if (medicosNovos.length === 0) return;
    const medicos_id = medicosNovos.map((medico) => medico.dados.medico_id);
    const response = await api(`${ENDPOINT}/schedule/getAvaliacoesMedicos`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ medicos_id }),
    });
    await response.json().then(async function (postsData) {
      if (!postsData) return [];
      setAvaliacoesAtualizadas((prev) => [...prev, ...postsData]);
    });
  }

  async function fetchProximasData() {
    try {
      const response = await api(`${ENDPOINT}/schedule/searchProximasDatas`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          username: paciente.cpf,
          especialidadeId: agendamento.especialidadeId,
          paciente_id: user.id_cliente_deovita,
        }),
      });
      await response.json().then(async function (postsData) {
        if (!postsData) {
          setProximasDatas([]);
          return [];
        }
        setAgendamento((prev) => ({
          ...prev,
          atendimento_gratuito: postsData?.telemedicina?.telemedicina_especialista_gratis,
          faltou: postsData.falta,
          plano_id: postsData?.telemedicina?.plano_id,
          pronto_atendimento_gratis: postsData?.telemedicina?.pronto_atendimento_gratis,
          telemedicina_especialista_gratis: postsData?.telemedicina?.telemedicina_especialista_gratis,
          especialidades: postsData?.especialidades,
          especialista: true,
        }));
        const allDatas = Object.values(postsData.data || {}).flat();
        setProximasDatas(allDatas);
      });
    } catch (error) {
      console.log('Erro ao buscar próximas datas:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function fetchInitialMedicos() {
      if (!paciente) return;
      setLoading(true);
      let urlPart;
      if (user.coords != null) {
        urlPart = `&lat=${user.coords.latitudeAtual}&lng=${user.coords.longitudeAtual}`;
      } else {
        if (
          user.coordenadas_cidade &&
          user.coordenadas_cidade.latitude !== null &&
          user.coordenadas_cidade.latitude !== 'null' &&
          user.coordenadas_cidade.latitude !== undefined &&
          user.coordenadas_cidade.latitude !== 'undefined'
        ) {
          urlPart = `&lat=${user.coordenadas_cidade.latitude}&lng=${user.coordenadas_cidade.longitude}`;
        } else {
          urlPart = `&city=${user.cidade ? user.cidade : 'FORTALEZA'}`;
        }
      }
      let url = ENDPOINT + `/schedule/searchMedicos?page=1&limit=10${urlPart}&profession=Todos&typeList=Especialista&sexo=${paciente?.sexo}`;
      let data = await fetchData(url);
      setMedicos(data);
      setPage(1);
      setHasMore(data.length > 0);
      console.log('Medicos iniciais carregados:', data)
      if (data.length === 0) {
        setLoading(false);
        return;
      }

      await fetchAvaliacoes(data);
      setLoading(false);
      setLoadingAgendas(false);
      await fetchProximasData();
    }
    fetchInitialMedicos();
  }, [agendamento?.especialidadeId, paciente]);

  useEffect(() => {
    let busyStack = false;
    const handleScroll = async () => {
      if (!hasMore || loading) return;
      let urlPart;
      if (user.coords != null) {
        urlPart = `&lat=${user.coords.latitudeAtual}&lng=${user.coords.longitudeAtual}`;
      } else {
        if (
          user.coordenadas_cidade &&
          user.coordenadas_cidade.latitude !== null &&
          user.coordenadas_cidade.latitude !== 'null' &&
          user.coordenadas_cidade.latitude !== undefined &&
          user.coordenadas_cidade.latitude !== 'undefined'
        ) {
          urlPart = `&lat=${user.coordenadas_cidade.latitude}&lng=${user.coordenadas_cidade.longitude}`;
        } else {
          urlPart = `&city=${user.cidade ? user.cidade : 'FORTALEZA'}`;
        }
      }
      if (window.innerHeight + window.scrollY + 10 > document.body.offsetHeight && busyStack === false) {
        setFetchMore(true);
        busyStack = true;
        let nextPage = page + 1;
        let url =
          ENDPOINT + `/schedule/searchMedicos?page=${nextPage}&limit=10${urlPart}&profession=Todos&typeList=Especialista&sexo=${paciente?.sexo}`;
        let data = await fetchData(url);
        setHasMore(data.length > 0);
        if (data.length === 0) {
          setFetchMore(false);
          return;
        }
        setMedicos((prev) => {
          const prevIds = new Set(prev.map((m) => m.dados.medico_id));
          const novosMedicos = data.filter((m) => !prevIds.has(m.dados.medico_id));
          return [...prev, ...novosMedicos];
        });
        await fetchAvaliacoes(data);
        setPage(nextPage);
        setFetchMore(false);
      }
    };
    window.removeEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [page, agendamento?.especialidadeId, hasMore, loading]);

  const filteredMedicos = useMemo(() => {
    if (!agendamento?.especialidadeId) {
      return medicos;
    }
    return medicos.filter((prop) => String(agendamento?.especialidadeId) === String(prop?.dados?.ambulatorio_tipo_consulta_id));
  }, [medicos, agendamento?.especialidadeId]);

  return {
    error,
    loading,
    especialista,
    modalEspecialista,
    setModalEspecialista,
    navigate,
    agendamento,
    atualizarAgendamento,
    avaliacaoMedia,
    setAvaliacaoMedia,
    medicos: filteredMedicos,
    avaliacoes_atualizadas,
    assinante,
    setMedicoSelecionado,
    user,
    toastInadimplente,
    proximasDatas,
    loadingAgendas,
  };
};

export default useModelMedicos;
